import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control, useFieldArray } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { BACKGROUND_FIELD_DESCRIPTIONS } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { useFormDescriptions } from '#apps/client-web/features/settings/hooks/use-form-descriptions';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import { Fab } from '#packages/web/foundations/buttons/fab';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { MessagePage } from '#packages/web/layouts/page/templates/message-page';
import { Message } from '#packages/web/foundations/feedback/message';

export type AuditorTeamRolesFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const AuditorTeamRolesFormDialog: FC<
  PropsWithChildren<AuditorTeamRolesFormDialogProps>
> = ({ open, control, onClose }) => {
  const getDescription = useFormDescriptions(BACKGROUND_FIELD_DESCRIPTIONS);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'general.auditorTeam',
  });

  const deleteAuditor = useDialog<number>();

  return (
    <SubFormDialog title="Auditor Team Roles" open={open} onClose={onClose}>
      <Stack spacing={3} sx={{ pb: 10 }}>
        {fields.length === 0 && (
          <MessagePage
            message={<Message>No auditor added yet. Tap + to add one.</Message>}
          />
        )}
        {fields.map((field, index) => (
          <Box key={field.id}>
            <Stack spacing={2}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Auditor {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="remove auditor"
                  onClick={() => deleteAuditor.open(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.auditorTeam.${index}.name`}
                    rules={{ maxLength: 120 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Name"
                          type="text"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    control={control}
                    name={`general.auditorTeam.${index}.tasks`}
                    rules={{ maxLength: 120 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Tasks"
                          multiline
                          rows={3}
                          type="text"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message ?? getDescription(field.name)}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Stack>
            {index < fields.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        ))}
      </Stack>

      <Fab
        tooltip="Add auditor"
        onClick={() => append({ name: '', tasks: '' })}
      />

      <DeleteConfirmationDialog
        open={deleteAuditor.isOpen}
        dialogTitle="Remove Auditor"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          remove(deleteAuditor.data!);
          deleteAuditor.close();
        }}
        onCancel={() => deleteAuditor.close()}
      />
    </SubFormDialog>
  );
};
